import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from "@stencil/core";

import { StzhSortableDisableEvent, StzhSortableUpdateEvent } from "../../index";

import scrollIntoView from "scroll-into-view-if-needed";
import Sortable from "../../libraries/sortablejs/sortable.complete.esm.js"; // import { cloneWrappedStzhElement } from "../../utils/utils";
import { StzhSortableLocalizedText } from "./stzh-sortable.localization";

const CLASS_ITEM = "is-sortable-item";
const CLASS_GHOST = "is-ghost";
const CLASS_SELECTED = "is-selected";
const CLASS_CHOSEN = "is-chosen";
const CLASS_DRAG = "is-drag";

const CLASS_PARENT_SELECTED = "has-item-selected";

let sortableCounter = 0;

/**
 * @slot - Slot for any elements
 */
@Component({
  tag: "stzh-sortable",
  styleUrl: "stzh-sortable.scss",
  scoped: true,
})
export class StzhSortable {
  /** Translation strings. */
  @Prop() localization: StzhSortableLocalizedText;

  /** Wether sorting is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** HTML attribute that is used by the `toArray()` method */
  @Prop() dataIdAttribute: string = "data-id";

  /** Container  */
  @Prop() container: HTMLElement | Function = () => {
    return this.containerElement?.firstElementChild;
  };

  // const [clonedElement] = cloneWrappedStzhElement(dragElement);
  // return clonedElement;
  /** Algorithm how the ghost element will be cloned */
  @Prop() ghostClone: Function = (dragElement: HTMLElement) => {
    return dragElement.cloneNode(true);
  };

  /** Filtered elements that should prevent dragging inside the drag item elements */
  @Prop() filterElements: any[] | string = [
    "[data-stzh-sortable-filtered]",
    "[sortable-disabled]",
    "button",
    "a[href]",
    "input",
    "select",
    "textarea",
    "label",
    "iframe",
    "details",
    "area",
    "stzh-sortable",
  ];

  private _filterElements: any[];

  @Element() element: HTMLStzhSortableElement;

  /** Serializes the sortable's item data-id's (dataIdAttr option) into an array of string */
  @Method()
  async toArray(): Promise<string[]> {
    return this.sortable.toArray();
  }

  /** Sorts the elements according to the array */
  @Method()
  async sort(order: String[], useAnimation: boolean = true) {
    return this.sortable.sort(order, useAnimation);
  }

  /** Move item */
  @Method()
  async moveItem(item: HTMLElement, direction: "up" | "down") {
    if (
      !item ||
      (!item.nextElementSibling && direction === "down") ||
      (!item.previousElementSibling && direction === "up")
    ) {
      return;
    }

    this.resetItemOnBlur = false;

    if (direction === "up") {
      item.previousElementSibling.before(item);
    } else if (direction === "down") {
      item.nextElementSibling.after(item);
    }

    this.updateAriaLive(this.localization.moved, item);
    this.focusItem(item);

    const currentIndex = Array.from(item.parentElement?.children).indexOf(item);

    this.stzhUpdate.emit({
      component: "stzh-sortable",
      data: await this.toArray(),
      item,
      newIndex: currentIndex,
      oldIndex: currentIndex + (direction === "up" ? 1 : -1),
    });

    this.resetItemOnBlur = true;
  }

  @Watch("disabled")
  disabledWatcher(newValue: boolean) {
    if (newValue) {
      this.setAttributes();
    } else {
      this.unsetAttributes();
    }

    this.stzhDisable.emit({
      component: "stzh-sortable",
      disabled: newValue,
    });
  }

  @Watch("filterElements")
  filterElementsWatcher(newValue: any[] | string) {
    if (typeof newValue === "string") {
      this._filterElements = JSON.parse(newValue);
    } else {
      this._filterElements = newValue;
    }
  }

  /** Update event */
  @Event() stzhUpdate: EventEmitter<StzhSortableUpdateEvent>;

  /** Disable event */
  @Event() stzhDisable: EventEmitter<StzhSortableDisableEvent>;

  private sortable: Sortable;
  private sortableId: string;
  private containerElement: HTMLElement;
  private ariaLiveElement: HTMLElement;
  private observer: MutationObserver;
  private selectedItem: HTMLElement = null;
  private resetItemOnBlur: boolean = false;
  private lastIndex: number;

  private getContainer(): HTMLElement {
    return typeof this.container === "function" ? this.container() : this.container;
  }

  private updateAriaLive(string: string, item: HTMLElement = this.selectedItem) {
    if (!this.getContainer()) {
      return;
    }

    if (item) {
      const title = item.getAttribute("aria-label") || "";
      const index = this.getIndexOfSelectedItem(item);

      string = string.replace("{title}", title).replace("{current}", (index + 1).toString());
    }

    const total = this.getContainer().children.length;

    string = string.replace(/\{total\}/gi, total.toString());

    this.ariaLiveElement.textContent = string;
  }

  private getIndexOfSelectedItem(item: HTMLElement = this.selectedItem) {
    if (item) {
      return Array.from(item.parentElement.children).indexOf(item);
    }
  }

  private resetActiveItem(moveToOriginalPosition: boolean = false) {
    if (!this.selectedItem) {
      return;
    }

    const currentIndex = this.getIndexOfSelectedItem();
    const container = this.selectedItem.parentElement;

    if (moveToOriginalPosition && this.lastIndex !== currentIndex) {
      if (this.lastIndex > currentIndex) {
        container.children[this.lastIndex].after(this.selectedItem);
      } else {
        container.children[this.lastIndex].before(this.selectedItem);
      }

      this.selectedItem.focus();
    }

    container.classList.remove(CLASS_PARENT_SELECTED);
    this.selectedItem.classList.remove(CLASS_SELECTED);
    this.selectedItem = null;
    this.lastIndex = null;
  }

  private focusItem(item: HTMLElement | null) {
    if (!item) {
      return;
    }

    item.focus();

    const flyingfocusElement = document.querySelector("stzh-flyingfocus");
    if (flyingfocusElement) {
      flyingfocusElement.reposition();
    }

    scrollIntoView(item, {
      scrollMode: "if-needed",
      block: "nearest",
      inline: "nearest",
    });
  }

  private setAttributes() {
    if (this.getContainer() && this.getContainer()?.children) {
      Array.from(this.getContainer()?.children).forEach(child => {
        child.classList.add(CLASS_ITEM);

        child.addEventListener("blur", this.onBlur);
        child.addEventListener("keydown", this.onKeydown);

        child.setAttribute("tabindex", "0");
        child.setAttribute("aria-describedby", `${this.sortableId}-description`);
      });
    }
  }

  private unsetAttributes() {
    if (this.getContainer() && this.getContainer()?.children) {
      Array.from(this.getContainer()?.children).forEach(child => {
        child.classList.remove(CLASS_ITEM);

        child.removeEventListener("blur", this.onBlur);
        child.removeEventListener("keydown", this.onKeydown);

        child.removeAttribute("tabindex");
        child.removeAttribute("aria-describedby");
      });
    }
  }

  private init = () => {
    if (!this.getContainer() || this.disabled) {
      return;
    }

    this.setAttributes();
  };

  private updated = async (event?: any) => {
    let newIndex: number;
    let oldIndex: number;
    let item: HTMLElement;

    if (event) {
      newIndex = event.newIndex;
      oldIndex = event.oldIndex;
      item = event.item;
    } else {
      newIndex = this.getIndexOfSelectedItem();
      oldIndex = this.lastIndex;
      item = this.selectedItem;
    }

    if (newIndex !== oldIndex) {
      this.stzhUpdate.emit({
        component: "stzh-sortable",
        data: await this.toArray(),
        item,
        newIndex,
        oldIndex,
      });
    }
  };

  private onKeydown = (event: KeyboardEvent) => {
    const currentTarget = event.currentTarget as HTMLElement;
    const target = event.target as HTMLElement;

    if (!this.getContainer() || this.disabled || currentTarget !== target) {
      return;
    }

    // const isFilteredElement = target.matches(this._filterElements.join(', '));
    // if (event.key === " " && !isFilteredElement) {

    if (event.key === " ") {
      event.preventDefault();

      if (!this.selectedItem) {
        this.selectedItem = currentTarget;
        this.selectedItem.parentElement.classList.add(CLASS_PARENT_SELECTED);
        this.selectedItem.classList.add(CLASS_SELECTED);
        this.lastIndex = this.getIndexOfSelectedItem();
        this.resetItemOnBlur = true;
        this.updateAriaLive(this.localization.started);
      } else {
        this.resetItemOnBlur = false;
        this.updateAriaLive(this.localization.dropped);
        this.updated();
        this.resetActiveItem();
      }
    } else if (event.key === "Tab" && this.selectedItem && this.selectedItem === event.currentTarget) {
      event.preventDefault();
    } else if (event.key === "Escape" && this.selectedItem) {
      this.resetItemOnBlur = false;
      this.updateAriaLive(this.localization.canceled);
      this.resetActiveItem(true);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();

      if (this.selectedItem) {
        this.moveItem(this.selectedItem, "up");
      } else {
        this.focusItem(currentTarget.previousElementSibling as HTMLElement);
      }
    } else if (event.key === "ArrowDown") {
      event.preventDefault();

      if (this.selectedItem) {
        this.moveItem(this.selectedItem, "down");
      } else {
        this.focusItem(currentTarget.nextElementSibling as HTMLElement);
      }
    }
  };

  private onBlur = () => {
    if (this.resetItemOnBlur) {
      this.resetItemOnBlur = false;
      this.updateAriaLive(this.localization.dropped);
      this.updated();
      this.resetActiveItem();
    }
  };

  async componentWillLoad() {
    this.sortableId = `stzh-sortable-${sortableCounter++}`;
    this.filterElementsWatcher(this.filterElements);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "sortable");
    }
  }

  componentWillUpdate() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  componentDidRender() {
    if (this.sortable) {
      return;
    }

    // see available option in https://github.com/SortableJS/Sortable#options
    this.sortable = new Sortable(this.getContainer(), {
      animation: 150,
      filter: this._filterElements.join(", "),
      ghostClass: CLASS_GHOST,
      chosenClass: CLASS_CHOSEN,
      dragClass: CLASS_DRAG,
      dataIdAttr: this.dataIdAttribute,
      preventOnFilter: false,
      forceFallback: true,
      delay: 200,
      delayOnTouchOnly: true,
      disabled: this.disabled,
      ghostClone: this.ghostClone,
      // changed sorting within list
      onUpdate: this.updated,
    });
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.unsetAttributes();

    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  render() {
    const classes = {
      "stzh-sortable": true,
      "stzh-sortable--disabled": this.disabled,
    };

    return (
      <Host>
        <div class={classes}>
          {!this.disabled && (
            <div id={`${this.sortableId}-description`} class="stzh-sortable__description">
              {this.localization.description}
            </div>
          )}
          <div
            ref={el => (this.ariaLiveElement = el as HTMLDivElement)}
            aria-live="assertive"
            class="stzh-sortable__aria-live"
          ></div>

          <div ref={el => (this.containerElement = el as HTMLDivElement)} class="stzh-sortable__container">
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
